import { useEffect, useState, useRef } from 'react';

/**
 * Phase 2J Realtime Phase 3 — Spectator Reaction Stream.
 *
 * Ambient floating-emoji overlay that bubbles reactions from any match
 * in a tournament. Builds shared spectator energy without becoming chatty.
 *
 * Mounts in the bottom-right of any tournament-aware page. Auto-fades.
 */
export default function ArenaxSpectatorStream({ incomingReaction, position = 'bottom-right' }) {
  const [bubbles, setBubbles] = useState([]);
  const seenRef = useRef(new Set());

  useEffect(() => {
    if (!incomingReaction || !incomingReaction.emoji) return;
    // Dedupe by user+emoji+at (avoid double-render of same payload)
    const key = `${incomingReaction.user_id || 'anon'}-${incomingReaction.emoji}-${incomingReaction.at}`;
    if (seenRef.current.has(key)) return;
    seenRef.current.add(key);
    // Cap memo set
    if (seenRef.current.size > 200) {
      seenRef.current = new Set(Array.from(seenRef.current).slice(-100));
    }
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    const xOffset = Math.random() * 80 - 40; // -40..+40 px horizontal drift
    const newBubble = {
      id,
      emoji: incomingReaction.emoji,
      userName: incomingReaction.user_name,
      xOffset,
    };
    setBubbles(prev => [...prev.slice(-19), newBubble]); // cap to 20 on screen
    // Auto-remove after animation
    setTimeout(() => {
      setBubbles(prev => prev.filter(b => b.id !== id));
    }, 4000);
  }, [incomingReaction]);

  const posClass =
    position === 'bottom-right' ? 'right-4 sm:right-8 bottom-24' :
    position === 'bottom-left' ? 'left-4 sm:left-8 bottom-24' :
    'right-4 bottom-24';

  return (
    <div
      data-testid="arenax-spectator-stream"
      className={`pointer-events-none fixed ${posClass} z-40 w-32 h-72 overflow-visible`}
      aria-hidden="true">
      {bubbles.map(b => (
        <div key={b.id}
          data-testid={`arenax-spectator-bubble-${b.id}`}
          className="absolute bottom-0 left-1/2 -translate-x-1/2"
          style={{
            animation: 'arenax-spectator-rise 4s ease-out forwards',
            '--x-offset': `${b.xOffset}px`,
          }}>
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-slate-950/85 border border-amber-500/30 backdrop-blur-sm shadow-lg shadow-amber-500/10">
            <span className="text-base leading-none">{b.emoji}</span>
            {b.userName && (
              <span className="text-[10px] font-bold text-slate-300 max-w-[80px] truncate">
                {b.userName.split(' ')[0]}
              </span>
            )}
          </div>
        </div>
      ))}
      {/* Inline keyframes for the rise animation */}
      <style>{`
        @keyframes arenax-spectator-rise {
          0%   { opacity: 0; transform: translateX(calc(-50% + var(--x-offset, 0px))) translateY(0px) scale(0.7); }
          15%  { opacity: 1; transform: translateX(calc(-50% + var(--x-offset, 0px))) translateY(-12px) scale(1); }
          80%  { opacity: 0.95; transform: translateX(calc(-50% + var(--x-offset, 0px))) translateY(-180px) scale(1); }
          100% { opacity: 0; transform: translateX(calc(-50% + var(--x-offset, 0px))) translateY(-220px) scale(0.85); }
        }
      `}</style>
    </div>
  );
}
